import { useAppRouter } from '@/common/route/useAppRouter';
import { StringUtils } from '@/common/utils/stringUtils';
import { reactive, watch } from 'vue';
import { useRoute } from 'vue-router';

export type PropType = 'string' | 'number' | 'boolean';

export type PropDefinition<T extends PropType> = T extends 'string'
    ? { type: 'string'; default: string }
    : T extends 'number'
    ? { type: 'number'; default: number }
    : T extends 'boolean'
    ? { type: 'boolean'; default: boolean }
    : never;

export type QueryDefinition = {
    [key: string]: PropDefinition<PropType>;
};

export type ExtractedParams<T extends QueryDefinition> = {
    [K in keyof T]: T[K]['default'];
};

export function useRouteQuery<T extends QueryDefinition>({
    definition,
    name,
    onChange
}: {
    definition: T;
    name?: string;
    onChange?: () => void | Promise<void>;
}) {
    const route = useRoute();
    const router = useAppRouter();
    const query = reactive({}) as ExtractedParams<T>;

    const routeName = name || route.name;

    const syncQueryFromRoute = (): boolean => {
        if (routeName != route.name) {
            return false;
        }

        let updated = false;
        let missingQuery = false;

        for (const key in definition) {
            const raw = route.query[key];
            if (raw === undefined) {
                missingQuery = true;
            }

            let value: any;
            const def = definition[key];
            switch (def.type) {
                case 'string':
                    value = raw !== undefined ? String(raw) : def.default;
                    break;
                case 'number':
                    value = !StringUtils.isEmpty(raw?.toString()) ? Number(raw) : def.default;
                    break;
                case 'boolean':
                    value = !StringUtils.isEmpty(raw?.toString()) ? raw === 'true' : def.default;
                    break;
            }

            const isEmptyToNull = query[key] === '' && value === null;
            if (query[key] !== value && !isEmptyToNull) {
                updated = true;
                query[key] = value;
            }
        }

        if (missingQuery) {
            updateQueryToRoute(false);
        }

        return updated;
    };

    const updateQueryToRoute = (pushToHistory: boolean = true) => {
        const newQuery: Record<string, any> = {};

        for (const key in definition) {
            const val = query[key];
            if (val !== undefined && val !== null) {
                newQuery[key] = String(val);
            } else {
                newQuery[key] = StringUtils.Characters.Empty;
            }
        }

        if (pushToHistory) {
            router.push({ query: { ...route.query, ...newQuery } });
        } else {
            router.replace({ query: { ...route.query, ...newQuery } });
        }
    };

    syncQueryFromRoute();

    watch(
        () => route.query,
        async () => {
            if (syncQueryFromRoute() && onChange) {
                await onChange();
            }
        }
    );

    return { query, syncQueryFromRoute, updateQueryToRoute };
}
